import React from 'react'
import {defineMessages, FormattedDate} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import {getSuggestionSearchValue} from 'modules/searchBox/selectors'
import {dateStringToMiddayDate} from 'utils'

import {NoStyleButton} from '@vio/atlas/helpers/NoStyleButton'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {SuggestionItemDataType} from '@vio/core/src/autocomplete/types'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'
import {guestPickerMessages} from '@vio/shared/src/search/GuestPicker/guestPickerMessages'

import {
  StyledListItem,
  SuggestionTextElement,
  SuggestionTextElementDetail,
  SuggestionTextWrapper
} from './styles'

const TYPE_TO_ICON_MAP = {
  history: 'Clock',
  area: 'Pin',
  property: 'Building',
  airport: 'PlaneTakeOff',
  station: 'Bus',
  city: 'Buildings',
  country: 'Pin',
  address: 'Street',
  close: 'Close'
}

const getIcon = (type: string | null | undefined = 'place') => {
  return <Icon name={TYPE_TO_ICON_MAP[type]} size="md" />
}

const renderShortFormattedDate = (value: string): React.ReactNode => (
  <FormattedDate
    value={dateStringToMiddayDate(value)}
    month="short"
    day="2-digit"
  />
)

const placeTypeFormattedMessages = defineMessages({
  city: {
    id: 'city',
    defaultMessage: 'City'
  },
  area: {
    id: 'area',
    defaultMessage: 'Area'
  },
  property: {
    id: 'property',
    defaultMessage: 'Property'
  },
  airport: {
    id: 'airport',
    defaultMessage: 'Airport'
  },
  station: {
    id: 'station',
    defaultMessage: 'Station'
  }
})

interface Props {
  suggestionDetails: SuggestionItemDataType
  isFocused?: boolean
  onUserHistoryCloseIconClick?: () => void
}
export const SuggestionItem: React.FC<Props> = ({
  suggestionDetails,
  isFocused,
  onUserHistoryCloseIconClick
}) => {
  const freeTextString = useSelector(getSuggestionSearchValue)

  const {
    objectName,
    objectParentName,
    objectPlaceTypeName,
    checkIn,
    checkOut,
    rooms,
    isUserSearchHistory,
    objectType,
    isFreeTextSearch
  } = suggestionDetails

  const translatedPlaceType = placeTypeFormattedMessages[objectPlaceTypeName]

  const handleCloseClick = e => {
    onUserHistoryCloseIconClick?.()
    e.stopPropagation()
  }

  const {numberOfAdults, numberOfChildren, numberOfRooms} =
    roomsToConfigurationObject(rooms)

  const numberOfGuests = numberOfAdults + numberOfChildren

  // Render user search history
  if (isUserSearchHistory) {
    return (
      <StyledListItem
        as="div"
        dataId="DestinationSuggestion--history"
        className={cx(isFocused && '--is-focused')}
        prefix={getIcon('history')}
        title={
          <SuggestionTextWrapper>
            <SuggestionTextElement>
              {objectName || (
                <FormattedMessageWrapper
                  id="mapArea"
                  defaultMessage="Map area"
                />
              )}
            </SuggestionTextElement>
            <SuggestionTextElementDetail>
              {renderShortFormattedDate(checkIn)}
              {` - `}
              {renderShortFormattedDate(checkOut)}
              {` · `}
              <FormattedMessageWrapper
                {...guestPickerMessages.guest}
                values={{count: numberOfGuests}}
              />
              {', '}
              <FormattedMessageWrapper
                {...guestPickerMessages.room}
                values={{count: numberOfRooms}}
              />
            </SuggestionTextElementDetail>
          </SuggestionTextWrapper>
        }
        suffix={
          <NoStyleButton onClick={handleCloseClick}>
            {getIcon('close')}
          </NoStyleButton>
        }
      />
    )
  }

  // Render free text search only on SRP
  if (isFreeTextSearch) {
    return (
      <StyledListItem
        as="div"
        dataId="DestinationSuggestion--freeText"
        className={cx(isFocused && '--is-focused')}
        prefix={<Icon name="SearchAi" size="md" />}
        title={
          <SuggestionTextWrapper>
            <SuggestionTextElement>
              <span>{freeTextString}</span>
            </SuggestionTextElement>
          </SuggestionTextWrapper>
        }
      />
    )
  }

  return (
    <StyledListItem
      as="div"
      dataId="DestinationSuggestion--place"
      className={cx(isFocused && '--is-focused')}
      prefix={getIcon(objectPlaceTypeName)}
      title={
        <SuggestionTextWrapper>
          <SuggestionTextElement>
            <span dangerouslySetInnerHTML={{__html: objectName}} />
          </SuggestionTextElement>
          {objectType !== 'address' && (
            <SuggestionTextElementDetail>
              {objectParentName}
            </SuggestionTextElementDetail>
          )}
        </SuggestionTextWrapper>
      }
      suffix={
        translatedPlaceType && (
          <Text variant="bodyS">
            <FormattedMessageWrapper {...translatedPlaceType} />
          </Text>
        )
      }
    />
  )
}
